import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, View } from 'react-native';
import { Controller } from 'react-hook-form';
import styled from 'styled-components/native';

import { Container, Input, Results } from 'app/src/components/Autocomplete';
import { AuthContext } from 'app/src/contexts/AuthContext';
import { Text } from 'app/src/styles';

import { Field, FieldLabel, errorsFor } from './inputs';

const ResultContainer = styled(Pressable)`
  flex-direction: row;
  column-gap: 10px;
  margin: 5px;
`;

const Name = styled(View)`
  flex-direction: row;
  gap: 10px;
`;

const NameText = styled(Text)`
  font-weight: 600;
`;

const Result = ({ result, handleSelect }) => {
  return (
    <ResultContainer onPress={() => handleSelect(result)}>
      <Name>
        <NameText>{result.name}</NameText>
        <Text>{result.accountGroupDescription}</Text>
      </Name>
    </ResultContainer>
  );
};

const AccountAutocomplete = props => {
  const { field, accounts, preferredTypes, preferredOnly } = props;

  const selectedAccount = accounts.find(account => account.id === field.value);

  const [originalValue, _setOriginalValue] = useState(selectedAccount);
  const [term, setTerm] = useState(selectedAccount?.name || '');
  const [searching, setSearching] = useState(false);
  const [blurTimeout, setBlurTimeout] = useState();
  const [hasBlurred, setHasBlurred] = useState(false)
  const [offsets, setOffsets] = useState({ top: 0, left: 0 });

  const ref = useCallback(element => {
    if (element) {
      setOffsets(element.getBoundingClientRect());
    }
  }, []);

  const handleChange = value => {
    if (blurTimeout) {
      window.clearTimeout(blurTimeout);
      setBlurTimeout(null);
    }

    setTerm(value);
    if (!searching) { setSearching(true); }

    field.onChange(null);
  };

  const handleKeyPress = e => {
    if ('Enter' === e.nativeEvent.key) {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (hasBlurred) {
      if (!field.value) { setTerm(''); }
      setSearching(false);
      setHasBlurred(false);
    }
  }, [hasBlurred, field]);

  const handleBlur = () => {
    setBlurTimeout(window.setTimeout(() => setHasBlurred(true), 200));
    field.onBlur();
  };

  const handleSelect = result => {
    field.onChange(result.id);
    setTerm(result.name);
    setSearching(false);
  };

  let results = accounts.filter(account => {
    if (account.hiddenAt && originalValue !== account.id) {
      return false;
    }

    return account.name.toLowerCase().includes(term.toLowerCase());
  });

  const preferred = [];
  const nonPreferred = [];

  if (preferredTypes && preferredTypes.length) {
    results.forEach(result => {
      if (preferredTypes.includes(result.type)) {
        preferred.push(result);
      } else if (!preferredOnly) {
        nonPreferred.push(result);
      }
    });

    results = [...preferred, ...nonPreferred];
  }

  const styles = {
    maxWidth: 500,
    top: offsets.top + 12,
    left: offsets.left,
  };

  return (
    <Container style={{ marginBottom: 0 }}>
      <div ref={ref} />
      <Input
        value={term}
        style={{ marginLeft: 0 }}
        onChangeText={handleChange}
        onKeyPress={handleKeyPress}
        onBlur={handleBlur}
      />
      {searching && term.length > 0 && results.length > 0 && (
        createPortal(
          <Results style={styles}>
            {results.map(result => (
              <Result
                key={`${result.__typename}-${result.id}`}
                result={result}
                handleSelect={handleSelect}
              />
            ))}
          </Results>, document.body
        )
      )}
    </Container>
  );
};

const AccountInput = props => {
  const { accounts } = useContext(AuthContext);

  const {
    name, label, hint, control, rules, errors, inline,
    preferredTypes, preferredOnly,
  } = props;

  if (!accounts) { return null; }

  return (
    <Field inline={inline} errors={errorsFor(errors, name)}>
      {!inline && (
        <FieldLabel
          name={name}
          label={label}
          hint={hint}
          errors={errors}
        />
      )}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <AccountAutocomplete
            field={field}
            accounts={accounts}
            preferredTypes={preferredTypes}
            preferredOnly={preferredOnly}
          />
        )}
      />
    </Field>
  );
};

export default AccountInput;
